import {AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {SearchFilterComponent} from '@shared/components/search-filter/search-filter.component';
import {MatTableDataSource} from '@angular/material/table';
import {SchoolingTuitionService} from '@shared/services/schooling-tuition.service';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {TranslationService} from '@shared/services/translation.service';
import {checkPageSize, DEFAULT_PAGE_SIZE, loadSavedFilter, PAGE_SIZE_OPTIONS, pageChange} from '@shared/utils/utils';
import {merge} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {Topic} from '@shared/models/topic.model';
import {SchoolingTuition, SchoolingTuitionDetails} from '@shared/models/schooling-tuition.model';
import {ListViewComponent} from '@shared/components/list-view/list-view.component';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngxs/store';
import {PermissionService} from '@shared/services/permission.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {PermissionKeys} from '@shared/models/permission.model';
import {ConfirmDialogComponent, ConfirmDialogConfig} from '@shared/components/confirm-dialog/confirm-dialog.component';
import {AuthState} from '@shared/store/auth/auth.state';
import {SchoolingParticipantService} from '@shared/services/schooling-participant.service';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';


export interface SchoolingTuitionBookingConfig {
  multiple: boolean;
  selected: Topic[];
  topicIds: number[];
  employeeIds: [];
  onlyForDetails: false;
}

@Component({
    selector: 'saf-schooling-tuition-booking',
    templateUrl: './schooling-tuition-booking.component.html',
    styleUrls: ['./schooling-tuition-booking.component.scss'],
    standalone: false
})
export class SchoolingTuitionBookingComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(SearchFilterComponent) searchFilter: SearchFilterComponent;
  @ViewChild(ListViewComponent, {static: false}) listView: ListViewComponent;
  listName = 'SchoolingTuitionList';
  public tableColumns = ['select', 'topicName', 'tuitionDate', 'status', 'participantsInfo'];

  public dataSource = new MatTableDataSource<SchoolingTuitionDetails>();
  public loading = false;
  public totalCount = 0;
  public hasPermission = false;
  public tuitionId: number;
  public drawerFilterParams: any = {};
  public searchFilterParams: any = {};
  public trackById = (item) => item.id;

  constructor(
    public dialogRef: MatDialogRef<SchoolingTuitionBookingComponent>,
    private participantService: SchoolingParticipantService,
    private schoolingTuitionService: SchoolingTuitionService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private store: Store,
    private router: Router,
    private translationService: TranslationService,
    private changeDetectorRef: ChangeDetectorRef,
    private permissionService: PermissionService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public config: SchoolingTuitionBookingConfig,
    @Inject(PAGE_SIZE_OPTIONS) public pageSizeOptions,
    @Inject(DEFAULT_PAGE_SIZE) public pageSize,
  ) {
  }

  ngOnInit(): void {
    this.hasPermission = this.permissionService.hasPermission(PermissionKeys.SCHOOLING_TUITIONS);
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    merge(this.sort.sortChange, this.paginator.page).subscribe(() => this.loadTable());
    if(this.config.onlyForDetails) {
      this.tableColumns = this.tableColumns.filter(column => column !== 'select');
    }
  }

  ngAfterViewInit() {
    this.applyDrawerFilters(loadSavedFilter(this.listName));
    this.changeDetectorRef.detectChanges();
  }

  loadTable() {
    const params = {
      filter: {
        ...this.searchFilterParams,
        ...this.drawerFilterParams,
        showInternal: 1,
        showExternal: 1,
        topicIds: this.config.topicIds,
        statusText: 'openAndOverdue',
      },
      sort: this.sort.direction ? this.sort.active : '',
      dir: this.sort.direction,
      start: this.paginator.pageIndex * this.paginator.pageSize,
      limit: this.paginator.pageSize,
    };

    if (params['filter']['fastSearch']) {
     delete params['filter']['fastSearch'];
    }
    this.loading = true;

    this.schoolingTuitionService.getListDetail(params)
      .pipe(tap(() => this.loading = false))
      .subscribe((response) => {
        this.dataSource.data = response.data;
        this.totalCount = response.total;
        // if (response.data.length === 0) {
        //   this.router.
        //   navigate([ 'schooling/create'], {queryParams: {topicId: this.config.topicId, employeeIds: this.config.employeeIds.join(', ') }});
        //   this.dialogRef.close();
        //
        // }
      });
  }

  reloadTable() {
    SchoolingTuitionService.clearCache();
    this.loadTable();
  }

  applyDrawerFilters(filterParams) {
    this.drawerFilterParams = filterParams;
    this.loadTable();
  }

  applySearchFilter({param, value}) {
    this.searchFilterParams = {[param]: value};

    if (value === null || value === undefined) {
      delete this.searchFilterParams[param];
    }

    this.loadTable();
  }

  openTuitionDetails(e, item?: SchoolingTuitionDetails) {
    e.stopPropagation();
    if (this.hasPermission) {
     const employeeIds = Array.isArray(this.config.employeeIds)
        ? this.config.employeeIds
        : [this.config.employeeIds];

      this.router.navigate([ 'schooling/create'], {queryParams: {topicId: this.config.topicIds[0], employeeIds: employeeIds.join(', ') }});
      this.dialogRef.close();
    } else {
      this.returnPermissionError();
    }
  }

  getParticipantInfo(item: SchoolingTuitionDetails) {
    if (item.tuition.maxParticipants === 0) {
      if (item.participants.length > 0) {
        return item.participants.length.toString();
      } else {
        return '-';
      }
    }
    return item.participants.length + ' / ' + item.tuition.maxParticipants;
  }

  finish(e, tuition: SchoolingTuition) {
    e.stopPropagation();
    if (this.hasPermission) {
      const data = new ConfirmDialogConfig({
        title: this.translationService.translate('confirmFinishTitle'),
        message: this.translationService.translate('confirmFinishTuition'),
        confirmText: this.translationService.translate('save'),
      });

      this.dialog.open(ConfirmDialogComponent, {data})
        .afterClosed()
        .pipe(filter(confirmed => !!confirmed))
        .subscribe(() => {
          this.schoolingTuitionService.finish(tuition.id)
            .subscribe(() => this.reloadTable());
        });
    } else {
      this.returnPermissionError();
    }
  }

  returnPermissionError() {
    return this.snackBar.open(this.translationService.translate('permissionError'));
  }




  protected readonly pageChange = pageChange;

  checkDate(listItem: SchoolingTuitionDetails) {

    if(listItem.tuition.tuitionDate === null) {
      return true;
    }
    return new Date(listItem.tuition.tuitionDate).valueOf() > new Date().valueOf();
  }

  protected readonly checkPageSize = checkPageSize;

  closeDialog($event: MouseEvent) {
    this.dialogRef.close();
  }

  cancel() {
    this.dialogRef.close(null);
  }

  selectTuition(event: MatSlideToggleChange, row: SchoolingTuitionDetails) {
    if (this.tuitionId === row.tuition.id) {
      this.tuitionId = null;
    } else {
      this.tuitionId = row.tuition.id;
      if (row.tuition.maxParticipants > 0)  {
        if (this.config.employeeIds.length > row.tuition.maxParticipants - row.participants.length) {
          const data = new ConfirmDialogConfig({
            title: this.translationService.translate('maxParticipantsInfo'),
            message: this.translationService.translate('increaseParticipantsInfo'),
            confirmText: this.translationService.translate('confirm'),
          });

          this.dialog.open(ConfirmDialogComponent, {data})
            .afterClosed()
            .pipe(filter(confirmed => !!confirmed))
            .subscribe(() => {
              const url = this.router.serializeUrl(
                this.router.createUrlTree(['/schooling/' +  row.tuition.id]));
              window.open(url, '_blank');

            });
          // this.tuitionId = 0;
        }
      }
    }
  }

  submit() {
    const employee = this.store.selectSnapshot(AuthState.employee);
    if(this.tuitionId) {
      const params = {
        employeeIds: this.config.employeeIds,
        tuitionId: this.tuitionId,
        status: 1,
        assigneeEmployeeId: employee ? employee.id : 0,

      };

      this.loading = true;

      this.participantService.createMultiple(params)
        .pipe(tap(() => this.loading = false))
        .subscribe(() => {
          this.loadTable();
        });
      this.dialogRef.close(this.tuitionId);
    } else {
      this.snackBar.open(this.translationService.translate('pleaseSelect'));
    }

  }

  openInNewTab(row:  SchoolingTuitionDetails) {
    if(this.config.onlyForDetails) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/schooling/' +  row.tuition.id]));
      window.open(url, '_blank');
    }
  }
}
